import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { fetchProjectSubTypesThunk } from '../../data/thunks/projectConfigurationThunk';
import ScreenContainer from '../common/layout/ScreenContainer';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import Paper, { PaperHeader, PaperHeaderNav } from '../common/layout/Paper';
import Tabs from '../common/Tabs';
import {
	Box,
	Typography,
	TextField,
	InputAdornment,
	Chip,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
	ProjectSubType,
	ProjectSubTypeOption,
} from '../../data/models/projectConfiguration';
import { getIconFromGoogleMimeType } from '../../utils/company-utils';
import { styled } from 'styled-components';
import Button from '../common/buttons/Button';
import Table from '../common/Table';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';

interface TemplateTableRow {
	id: string;
	templateName: string;
	templateType: string;
	mimeType: string;
	template: any;
}

export default function ProjectSubTypeDetailsScreen() {
	const { projectSubTypeId } = useParams<{ projectSubTypeId: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const theme = useTheme();

	const { projectSubTypes, loading } = useSelector(
		(state: RootState) => state.projectConfigurationState
	);

	const [searchTerm, setSearchTerm] = useState('');
	const [currentTab, setCurrentTab] = useState('');

	// Find the current project sub type
	const currentSubType = useMemo(() => {
		return projectSubTypes.find(
			(subType) => subType.id === projectSubTypeId
		);
	}, [projectSubTypes, projectSubTypeId]);

	useEffect(() => {
		if (projectSubTypes.length === 0) {
			dispatch(fetchProjectSubTypesThunk());
		}
	}, [dispatch, projectSubTypes.length]);

	// Set the first tab as active when data loads
	useEffect(() => {
		if (
			currentSubType &&
			currentSubType.options.length > 0 &&
			!currentTab
		) {
			setCurrentTab(currentSubType.options[0].id);
		}
	}, [currentSubType, currentTab]);

	if (loading || !currentSubType) {
		return <ScreenBusySpinner />;
	}

	// Create panels for each sub type option
	const panels = currentSubType.options.map((option) => {
		const rulesCount = option.rules?.length || 0;

		return {
			label: (
				<TabLabelContainer>
					{option.display}
					{rulesCount > 0 && (
						<Chip
							label={rulesCount}
							size="small"
							color="primary"
							sx={{ ml: 1, fontSize: '0.75rem' }}
						/>
					)}
				</TabLabelContainer>
			),
			key: option.id,
			component: (
				<ProjectSubTypeOptionPanel
					option={option}
					searchTerm={searchTerm}
				/>
			),
			onclick: () => setCurrentTab(option.id),
		};
	});

	return (
		<PageWrapper>
			<Container gradientBorder>
				<Box
					sx={{
						width: '100%',
						backgroundColor: theme.palette.background.paper,
						borderRadius: 2,
						position: 'relative',
						flex: 1,
						height: 'calc(100vh - 98px)',
						padding: 1,
						overflow: 'hidden',
					}}
				>
					<PaperHeader>
						<PaperHeaderNav
							onClick={() => navigate('/project-sub-types')}
						>
							Project Sub Types <ChevronRightOutlinedIcon />
						</PaperHeaderNav>
						<SubTypeName>{currentSubType.display}</SubTypeName>
					</PaperHeader>

					{/* Tabs */}
					<Tabs
						panels={panels}
						currentPath={currentTab}
						variant="standard"
					/>
				</Box>
			</Container>
		</PageWrapper>
	);
}

interface ProjectSubTypeOptionPanelProps {
	option: ProjectSubTypeOption;
	searchTerm: string;
}

const ProjectSubTypeOptionPanel = ({
	option,
	searchTerm,
}: ProjectSubTypeOptionPanelProps) => {
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	// Get all templates from all rules in this option
	const allTemplates = useMemo(() => {
		const templates: any[] = [];
		option.rules?.forEach((rule) => {
			rule.templates?.forEach((template) => {
				// Avoid duplicates
				if (!templates.find((t) => t.id === template.id)) {
					templates.push(template);
				}
			});
		});
		return templates;
	}, [option.rules]);

	// Transform templates to table rows
	const transformedRows = useMemo((): TemplateTableRow[] => {
		let filteredTemplates = allTemplates;

		// Filter templates based on search term
		if (searchTerm) {
			filteredTemplates = allTemplates.filter((template) =>
				template.name.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		return filteredTemplates.map((template) => ({
			id: template.id,
			templateName: template.name,
			templateType: template.default ? 'Default' : 'Custom',
			mimeType: template.mimeType,
			template: template,
		}));
	}, [allTemplates, searchTerm]);

	const columns: GridColDef[] = [
		{
			field: 'templateName',
			headerName: 'Hidden Templates',
			flex: 2,
			minWidth: 300,
			renderCell: (params) => {
				const icon = getIconFromGoogleMimeType(params.row.mimeType);
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							style={{
								height: 20,
								marginRight: 10,
								objectFit: 'cover',
							}}
							src={icon.icon}
							alt={icon.alt}
						/>
						{params.value}
					</div>
				);
			},
		},
	];

	return (
		<OptionPanelContainer>
			{option.rules && option.rules.length > 0 ? (
				<>
					<Box sx={{ mb: 2 }}>
						<Typography variant="body2" color="text.secondary">
							The templates below will be hidden when creating a
							project with {option.display}.
						</Typography>
					</Box>
					{transformedRows.length > 0 ? (
						<TableContainer>
							<Table
								title=""
								columns={columns}
								rows={transformedRows}
								rowCount={transformedRows.length}
								loading={false}
								sortModel={sortModel}
								onSortModelChange={setSortModel}
								page={page}
								pageSize={pageSize}
								onPaginationModelChange={(model) => {
									setPage(model.page);
									setPageSize(model.pageSize);
								}}
								hideFooterPagination={false}
								hideFooter={false}
								autoHeight={false}
								boxSx={{
									height: 'calc(100vh - 260px)',
									'& .MuiDataGrid-row': {
										'&:hover': {
											backgroundColor: 'action.hover',
										},
									},
								}}
							/>
						</TableContainer>
					) : (
						<EmptyState>
							{searchTerm
								? 'No templates match your search.'
								: 'No templates found.'}
						</EmptyState>
					)}
				</>
			) : (
				<EmptyState>
					All templates will be created for {option.display}
				</EmptyState>
			)}
		</OptionPanelContainer>
	);
};

const PageWrapper = styled.div`
	display: flex;
	width: 100%;
	position: relative;
`;

const Container = styled(Paper)<{ $drawerOpen?: boolean }>`
	flex: 1;
	transition: width 0.3s ease-out;
	width: ${(props) => (props.$drawerOpen ? 'calc(100% - 360px)' : '100%')};
	margin-right: ${(props) => (props.$drawerOpen ? '348px' : '0')};
`;

// Styled components
const TabLabelContainer = styled.div`
	display: flex;
	align-items: center;
`;

const SubTypeName = styled.span`
	color: ${({ theme }) => theme.palette.text.primary};
`;

const OptionPanelContainer = styled(Box)`
	padding: 16px 0;
`;

const SectionHeader = styled(Box)`
	margin-bottom: 16px;
`;

const TableContainer = styled(Paper)`
	flex: 1;
`;

const EmptyState = styled(Typography)`
	text-align: center;
	color: ${({ theme }) => theme.palette.text.secondary};
	font-style: italic;
	padding: 32px;
`;
